// Pide `path` a la API ya y cada `intervalMs`, y guarda lo último que llegó.
//
// Lo que vuelve tarde no pisa lo nuevo: si `path` (o el token) cambió
// mientras un pedido estaba en vuelo, su respuesta se descarta. Antes, en
// Choferes en vivo, prender «solo los que reportan» y que llegara después la
// respuesta del pedido anterior mostraba la lista completa diez segundos
// más. Mientras un pedido está en vuelo, el refresco periódico no apila otro.
//
// `path` null: en pausa (no se pide nada y queda lo último que llegó).
import { useCallback, useEffect, useRef, useState } from "react";
import { requestJson } from "../lib/requestJson";

export type Polled<T> = { data: T | null; error: string | null; updatedAt: Date | null; reload: () => void };

export function usePolledJson<T>(path: string | null, intervalMs: number, token: string | null | undefined, pick: (json: any) => T): Polled<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);
  const pickRef = useRef(pick);
  pickRef.current = pick;
  // Sube cada vez que cambia lo que se pide (o al salir de la pantalla).
  const generation = useRef(0);
  const inFlight = useRef(false);

  const fetchNow = useCallback(async (force: boolean) => {
    if (path == null || (inFlight.current && !force)) return;
    const mine = generation.current;
    inFlight.current = true;
    try {
      const json = await requestJson(path, { token });
      if (mine !== generation.current) return;
      setData(pickRef.current(json));
      setUpdatedAt(new Date());
      setError(null);
    } catch (e: any) {
      if (mine === generation.current) setError(e.message);
    } finally {
      if (mine === generation.current) inFlight.current = false;
    }
  }, [path, token]);

  useEffect(() => {
    if (path == null) return;
    fetchNow(true);
    const t = window.setInterval(() => fetchNow(false), intervalMs);
    return () => { window.clearInterval(t); generation.current += 1; inFlight.current = false; };
  }, [fetchNow, intervalMs, path]);

  // «Actualizar» a mano: lo que esté en vuelo se descarta (sube la
  // generación) y se pide de nuevo, así el botón siempre hace algo y dos
  // respuestas de la misma generación no se cruzan.
  const reload = useCallback(() => { generation.current += 1; inFlight.current = false; fetchNow(true); }, [fetchNow]);
  return { data, error, updatedAt, reload };
}
